import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCartIcon, HeartIcon, StarIcon, EyeIcon } from 'lucide-react';
import { Product } from '../../types';
import { formatPrice } from '../../utils/formatters';
import { useCart } from '../../context/CartContext';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface ProductCardProps {
  product: Product;
  onQuickView?: (product: Product) => void;
}
export function ProductCard({
  product,
  onQuickView
}: ProductCardProps) {
  const {
    addItem
  } = useCart();
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
  };
  return <div className="group bg-white rounded-xl border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden" data-id="element-770" data-mp="7ci4cgrxsmej">
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-gray-50" data-id="element-771" data-mp="gdybmfngg8ej">
        <Link to={`/product/${product.slug}`} data-id="element-772" data-mp="gblntlv0n3tk">
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-773" data-mp="j9x677qih1ei" />
        </Link>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-774" data-mp="t68g5r2mpf5s">
          {product.discount && product.discount > 0 && <Badge variant="error" size="sm" data-id="element-775" data-mp="xag9k1k17jac">
              -{product.discount}%
            </Badge>}
          {!product.inStock && <Badge variant="default" size="sm" data-id="element-776" data-mp="125vmmra7tb5p">
              Out of Stock
            </Badge>}
        </div>

        {/* Quick actions */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-777" data-mp="1bx9fwhbkpeqe">
          <button className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-red-500 hover:bg-red-50 transition-colors" aria-label="Add to wishlist" data-id="element-778" data-mp="1gvksgzt963cb">
            <HeartIcon className="w-4 h-4" data-id="element-779" data-mp="1py3pv4e9rnr6" />
          </button>
          {onQuickView && <button onClick={e => {
          e.preventDefault();
          onQuickView(product);
        }} className="w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors" aria-label="Quick view" data-id="element-780" data-mp="3m1qlc2ua9mjo">
              <EyeIcon className="w-4 h-4" data-id="element-781" data-mp="3qzjnmksgtweo" />
            </button>}
        </div>

        {/* Add to cart button */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-782" data-mp="9hz5bc6psy91">
          <Button variant="primary" size="sm" fullWidth leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-784" data-mp="ihvxvow86h9d" />} onClick={handleAddToCart} disabled={!product.inStock} data-id="element-783" data-mp="iki33hvn8kof">
            Add to Cart
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4" data-id="element-785" data-mp="2kdfst4u4ix55">
        <Link to={`/product/${product.slug}`} data-id="element-786" data-mp="2fciams94633n">
          <p className="text-xs text-gray-500 mb-1" data-id="element-787" data-mp="2fdtf6uadvevt">{product.brand}</p>
          <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 hover:text-primary-600 transition-colors" data-id="element-788" data-mp="uty0b8qu7zuo">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-2" data-id="element-789" data-mp="m2fnn7t3o26c">
          <div className="flex items-center" data-id="element-790" data-mp="lzth9bhy3spp">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3.5 h-3.5 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} data-id="element-791" data-mp="q1y7e096q77l" data-mpi={i} />)}
          </div>
          <span className="text-xs text-gray-500" data-id="element-792" data-mp="h3chtl5aprxb">
            ({product.reviewCount.toLocaleString()})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-2" data-id="element-793" data-mp="354p5pfalcry">
          <span className="text-lg font-bold text-gray-900" data-id="element-794" data-mp="73vdp85ozm3g">
            {formatPrice(product.price)}
          </span>
          {product.originalPrice && product.originalPrice > product.price && <span className="text-sm text-gray-400 line-through" data-id="element-795" data-mp="21j6bekfx5lb8">
              {formatPrice(product.originalPrice)}
            </span>}
        </div>
      </div>
    </div>;
}